import { useCallback, useEffect, useMemo, useState } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { SepaForm } from '@/components/SepaForm'
import { QrCodeDisplay } from '@/components/QrCodeDisplay'
import { UserGuide } from '@/components/UserGuide'
import { parseUrlParams, type DisplayMode } from '@/lib/url-params'
import type { SepaQrData } from '@/lib/sepa-qr'
import { QrCode, BookOpen } from 'lucide-react'

/**
 * Main application component
 * Handles URL parameter parsing, mode switching, and component integration
 */
function App() {
  // Parse URL parameters on mount
  const urlParams = useMemo(() => parseUrlParams(), [])

  // Display mode from URL (read-only, set from URL params)
  const mode: DisplayMode = urlParams.mode

  // Form data state
  const [formData, setFormData] = useState<SepaQrData>({
    name: '',
    iban: '',
    ...urlParams.data
  })
  const [isValid, setIsValid] = useState(false)

  // Handle form data changes
  const handleDataChange = useCallback((data: SepaQrData, valid: boolean) => {
    setFormData(data)
    setIsValid(valid)
  }, [])

  // Update title based on beneficiary name
  useEffect(() => {
    if (formData.name) {
      document.title = `SEPA QR RTP - ${formData.name}`
    } else {
      document.title = 'SEPA QR Code Request-to-Pay'
    }
  }, [formData.name])

  // Clean mode: render only QR code (for embedding)
  if (mode === 'clean') {
    return (
      <QrCodeDisplay
        data={formData}
        isValid={isValid || urlParams.hasParams}
        cleanMode
      />
    )
  }

  // Share mode: render QR code with payment summary
  if (mode === 'share') {
    return (
      <QrCodeDisplay
        data={formData}
        isValid={isValid || urlParams.hasParams}
        shareMode
      />
    )
  }

  // Full mode: render complete interface
  return (
    <Tabs defaultValue="generator" className="min-h-screen bg-background safe-top safe-bottom flex flex-col">
      {/* Header with integrated navigation */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 sm:h-16 items-center justify-between px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          {/* Logo */}
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-sm">
              <QrCode className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
            <div className="flex flex-col">
              <h1 className="text-base sm:text-lg font-semibold tracking-tight text-foreground">
                SEPA QR Code
              </h1>
              <span className="text-[10px] sm:text-xs text-muted-foreground hidden sm:block">
                Request-to-Pay Generator
              </span>
            </div>
          </div>

          {/* Navigation Tabs */}
          <TabsList className="h-9 sm:h-10 bg-muted/50 p-1 rounded-lg">
            <TabsTrigger 
              value="generator" 
              className="px-3 sm:px-4 text-xs sm:text-sm gap-1.5 sm:gap-2 rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all"
            >
              <QrCode className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden xs:inline sm:inline">Generator</span>
            </TabsTrigger>
            <TabsTrigger 
              value="guide" 
              className="px-3 sm:px-4 text-xs sm:text-sm gap-1.5 sm:gap-2 rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all"
            >
              <BookOpen className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden xs:inline sm:inline">Guide</span>
            </TabsTrigger>
          </TabsList>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <div className="container px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 max-w-7xl mx-auto">
          {/* Generator Tab */}
          <TabsContent value="generator" className="mt-0">
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 sm:gap-6 lg:gap-8">
              {/* QR Display - Shown first on mobile */}
              <div className="xl:col-span-5 xl:order-2">
                <div className="xl:sticky xl:top-24">
                  <QrCodeDisplay
                    data={formData}
                    isValid={isValid}
                  />
                </div>
              </div>

              {/* Form */}
              <div className="xl:col-span-7 xl:order-1">
                <SepaForm
                  initialData={urlParams.hasParams ? urlParams.data : undefined}
                  onDataChange={handleDataChange}
                />
              </div>
            </div>
          </TabsContent>

          {/* Guide Tab - Full width */}
          <TabsContent value="guide" className="mt-0">
            <UserGuide />
          </TabsContent>
        </div>
      </main>
    </Tabs>
  )
}

export default App
